import React, { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Wine, Camera, CameraOff, SwitchCamera, Search, X, Mail, Phone, CalendarClock, LogOut, ChevronRight, CheckCircle2, History, Undo2 } from "lucide-react";
import { api, formatError, avatarSrc, fmtDate, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function DrinkScanner({ embedded = false }) {
  const { user, logout } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [facing, setFacing] = useState("environment");
  const [q, setQ] = useState("");
  const [members, setMembers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const scannerRef = useRef(null);
  const lastRef = useRef({ code: "", t: 0 });

  const loadMembers = useCallback((query = "") => {
    api.get(`/drinks/members?q=${encodeURIComponent(query)}`).then((r) => setMembers(r.data)).catch(() => {});
  }, []);
  const loadHistory = useCallback(() => {
    api.get("/drinks/redemptions").then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadMembers(""); loadHistory(); }, [loadMembers, loadHistory]);
  useEffect(() => () => { stopInternal(); }, []); // eslint-disable-line

  const search = (v) => { setQ(v); loadMembers(v.trim().length >= 2 ? v.trim() : ""); };

  const openMember = useCallback(async (uid) => {
    try { const r = await api.get(`/drinks/member/${uid}`); setDetail(r.data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }, []);

  const redeem = async () => {
    if (!detail || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/drinks/redeem", { user_id: detail.member.id });
      toast.success("Drink redeemed", { description: `${detail.member.name} · ${r.data.drink_left} left` });
      await openMember(detail.member.id);
      loadMembers(q.trim().length >= 2 ? q.trim() : "");
      loadHistory();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const undo = async (rid) => {
    try {
      await api.post(`/drinks/redemptions/${rid}/undo`);
      toast.success("Redemption undone — coupon restored");
      loadHistory();
      loadMembers(q.trim().length >= 2 ? q.trim() : "");
      if (detail) openMember(detail.member.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // ---------- scanner lifecycle ----------
  const onDecode = (text) => {
    const now = Date.now();
    if (lastRef.current.code === text && now - lastRef.current.t < 3000) return;
    lastRef.current = { code: text, t: now };
    let data;
    try { data = JSON.parse(text); } catch { toast.error("Unrecognized QR code"); return; }
    if (data.uid) { openMember(data.uid); return; }
    toast.error("Not a member QR code");
  };
  const startWith = async (f) => {
    try {
      const s = new Html5Qrcode("drink-reader");
      scannerRef.current = s;
      await s.start({ facingMode: f }, { fps: 10, qrbox: 250 }, onDecode, () => {});
      setScanning(true);
    } catch (e) { toast.error("Could not start camera. Use search instead."); }
  };
  const start = () => startWith(facing);
  const stopInternal = async () => {
    const s = scannerRef.current;
    if (s) { try { await s.stop(); await s.clear(); } catch (e) { /* noop */ } scannerRef.current = null; }
  };
  const stop = async () => { await stopInternal(); setScanning(false); };
  const switchCamera = async () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    if (scanning) { await stopInternal(); await startWith(next); }
  };

  const body = (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="drink-scanner-page">
      {/* Scanner */}
      <div>
        <div className="relative rounded-2xl overflow-hidden bg-black/40 border border-white/10 min-h-[260px]">
          <div id="drink-reader" className="w-full" />
          {!scanning && <span className="absolute inset-0 flex items-center justify-center text-[#A19DB0] text-sm pointer-events-none px-6 text-center">Tap “Start camera”, then scan a member's QR to redeem a drink.</span>}
        </div>
        {!scanning
          ? <button onClick={start} data-testid="drink-scan-start" className="w-full mt-3 flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full py-3 active:scale-[0.98] transition-transform"><Camera size={18} /> Start camera</button>
          : (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={switchCamera} data-testid="drink-scan-switch" className="flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-semibold bg-white/5 border border-white/10"><SwitchCamera size={16} /> Flip</button>
              <button onClick={stop} data-testid="drink-scan-stop" className="flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-semibold bg-white/5 border border-white/10 text-[#E57373]"><CameraOff size={16} /> Stop</button>
            </div>
          )}
      </div>

      {/* Manual search + list */}
      <div>
        <label className="text-xs text-[#A19DB0]">Search member by name or email</label>
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 mt-1">
          <Search size={18} className="text-[#A19DB0]" />
          <input value={q} onChange={(e) => search(e.target.value)} data-testid="drink-search-input" placeholder="Type to search, or browse members with coupons" className="bg-transparent outline-none flex-1 text-sm placeholder:text-white/30" />
        </div>
        <p className="text-[#A19DB0] text-xs mt-2">{q.trim().length >= 2 ? "Search results" : "Members with drink coupons"}</p>
        <div className="space-y-2 mt-2 max-h-[420px] overflow-y-auto pr-1">
          {members.length === 0 ? <p className="text-[#A19DB0] text-sm py-4">No members to show.</p> : members.map((m) => (
            <button key={m.id} onClick={() => openMember(m.id)} data-testid={`drink-member-${m.id}`} className="w-full text-left flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3 hover:border-[#D4AF37]/40 transition-colors">
              <img src={avatarSrc(m)} alt="" className="w-9 h-9 rounded-full object-cover" />
              <div className="flex-1 min-w-0"><p className="font-medium truncate text-sm">{m.name}</p><p className="text-[#A19DB0] text-xs truncate">{m.email}</p></div>
              <span className={`shrink-0 flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${m.drink_left > 0 ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-white/5 text-[#A19DB0]"}`}><Wine size={13} /> {m.drink_left}/{m.drink_total}</span>
              <ChevronRight size={16} className="text-[#A19DB0] shrink-0" />
            </button>
          ))}
        </div>
      </div>

      {/* Redeemed today */}
      <div className="lg:col-span-2" data-testid="drink-history">
        <div className="flex items-center gap-2 mt-2 mb-3">
          <History size={18} className="text-[#D4AF37]" />
          <h3 className="font-display text-lg font-semibold">Redeemed today</h3>
          <span className="text-[#A19DB0] text-sm">· {history.filter((h) => !h.undone).length}</span>
        </div>
        {history.length === 0 ? <p className="text-[#A19DB0] text-sm">No drinks redeemed yet today.</p> : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} data-testid={`drink-history-${h.id}`} className={`flex items-center gap-3 rounded-2xl border p-3 ${h.undone ? "bg-white/[0.02] border-white/5 opacity-60" : "bg-white/5 border-white/10"}`}>
                <Wine size={18} className={h.undone ? "text-[#A19DB0]" : "text-[#D4AF37]"} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${h.undone ? "line-through text-[#A19DB0]" : ""}`}>{h.member_name}</p>
                  <p className="text-[#A19DB0] text-xs truncate">{h.package_name} · {fmtDateTime(h.created_at)} · by {h.staff_name}</p>
                </div>
                {h.undone
                  ? <span className="text-xs text-[#A19DB0] shrink-0">Undone</span>
                  : user?.role === "super_admin" && <button onClick={() => undo(h.id)} data-testid={`drink-undo-${h.id}`} className="flex items-center gap-1 shrink-0 rounded-full bg-white/10 border border-white/10 px-3 py-1.5 text-sm text-[#E57373] active:scale-95 transition-transform"><Undo2 size={14} /> Undo</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="drink-detail-panel">
          <div className="bg-[#140F1E] border border-white/10 rounded-3xl p-6 w-full max-w-md max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <img src={avatarSrc(detail.member)} alt="" className="w-12 h-12 rounded-full object-cover" />
                <div className="min-w-0"><h3 className="font-display text-xl font-semibold truncate">{detail.member.name}</h3>
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-0.5 mt-1 ${detail.drink_left > 0 ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-white/5 text-[#A19DB0]"}`}><Wine size={12} /> {detail.drink_left}/{detail.drink_total} drink coupons</span>
                </div>
              </div>
              <button onClick={() => setDetail(null)} data-testid="drink-close-detail" className="text-[#A19DB0]"><X size={18} /></button>
            </div>

            <div className="space-y-1.5 text-sm">
              <p className="flex items-center gap-2 text-[#E8E4F0]"><Mail size={14} className="text-[#A19DB0]" /> {detail.member.email}</p>
              {detail.member.phone && <p className="flex items-center gap-2 text-[#E8E4F0]"><Phone size={14} className="text-[#A19DB0]" /> {detail.member.phone}</p>}
              {detail.member.join_date && <p className="flex items-center gap-2 text-[#A19DB0]"><CalendarClock size={14} /> Member since {fmtDate(detail.member.join_date)}</p>}
            </div>

            <h4 className="font-display text-sm font-semibold mt-5 mb-2 text-[#A19DB0] uppercase tracking-wide">Passes purchased</h4>
            {detail.passes.length === 0 ? <p className="text-[#A19DB0] text-sm">No passes with drink coupons.</p> : (
              <div className="space-y-2">
                {detail.passes.map((p) => (
                  <div key={p.id} data-testid={`drink-pass-${p.id}`} className="rounded-xl bg-white/5 border border-white/10 p-3">
                    <p className="font-medium text-sm">{p.package_name}{p.option ? ` · ${p.option}` : ""}</p>
                    <p className="text-[#A19DB0] text-xs mt-0.5">Purchased {fmtDateTime(p.purchased_at)}</p>
                    <p className="text-[#D4AF37] text-xs mt-0.5 flex items-center gap-1"><Wine size={12} /> {p.drink_left}/{p.drink_total} left</p>
                  </div>
                ))}
              </div>
            )}

            <button onClick={redeem} disabled={busy || detail.drink_left <= 0} data-testid="drink-redeem-btn" className="w-full mt-5 flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full py-3 disabled:opacity-50 active:scale-[0.98] transition-transform">
              {detail.drink_left <= 0 ? <>No coupons left</> : <><CheckCircle2 size={18} /> {busy ? "Redeeming…" : "Redeem 1 drink"}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-5"><Wine size={22} className="text-[#D4AF37]" /><h2 className="font-display text-3xl font-bold">Drink Scanner</h2></div>
        {body}
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] w-full bg-[#0D0B14] text-white">
      <header className="sticky top-0 z-30 bg-[#0D0B14]/90 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center justify-between px-5 py-3.5 max-w-4xl mx-auto">
          <div className="flex items-center gap-2">
            <Wine size={20} className="text-[#D4AF37]" />
            <div><p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] leading-none">Darkchata</p><h1 className="font-display text-lg font-bold leading-tight">Drink Scanner</h1></div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[#A19DB0] text-sm hidden sm:inline truncate max-w-[140px]">{user?.name}</span>
            <button onClick={logout} data-testid="drink-logout" className="flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3.5 py-2 text-sm text-[#E57373]"><LogOut size={15} /> Log out</button>
          </div>
        </div>
      </header>
      <main className="p-5 lg:p-8 max-w-4xl mx-auto w-full">{body}</main>
    </div>
  );
}
